import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Bell, BellOff } from "lucide-react";
import {
  currentSubscription, disablePush, enablePush, pushPermission, pushStatus, pushSupported,
} from "../api/push";
import { usePwa } from "../context/PwaContext";
import { ru } from "../locales/ru";

export const PushToggle = () => {
  const { showPushInstructions, permission: currentPermission, needsInstall, install } = usePwa();
  const [enabled, setEnabled] = useState(false);
  const [permission, setPermission] = useState(pushPermission());
  const [server, setServer] = useState(null);
  const [busy, setBusy] = useState(false);
  const supported = pushSupported();

  const refresh = useCallback(async () => {
    if (!supported) return;
    setPermission(pushPermission());
    const sub = await currentSubscription().catch(() => null);
    setEnabled(!!sub);
    const status = await pushStatus().catch(() => null);
    if (status) setServer(status);
  }, [supported]);

  useEffect(() => {
    refresh();
    window.addEventListener("push-state-changed", refresh);
    return () => window.removeEventListener("push-state-changed", refresh);
  }, [refresh, currentPermission]);

  if (needsInstall) return <div data-testid="push-install-profile" className="bg-gray-50 rounded-2xl p-4"><p className="font-semibold text-sm">{ru.pwa.pushTitle}</p><p data-testid="push-install-profile-hint" className="text-xs text-gray-500 mt-1">{ru.pwa.installForPush}</p><button data-testid="push-install-profile-button" onClick={install} className="text-sm font-semibold underline mt-3">{ru.pwa.install}</button></div>;
  if (!supported) return null;

  const toggle = async () => {
    setBusy(true);
    try {
      if (enabled) {
        await disablePush();
        toast.success(ru.pwa.pushOff);
      } else {
        await enablePush();
        toast.success(ru.pwa.pushOn);
      }
    } catch (err) {
      if (err?.message === "denied") showPushInstructions();
      else if (err?.message === "dismissed") toast.info(ru.pwa.promptDismissed);
      else if (err?.message === "install_required") install();
      else toast.error(ru.errors.generic);
    } finally {
      await refresh();
      window.dispatchEvent(new Event("push-state-changed"));
      setBusy(false);
    }
  };

  const denied = permission === "denied";
  const stateLine = denied
    ? ru.pwa.stateDenied
    : permission === "granted"
    ? enabled
      ? ru.pwa.stateOn(server?.subscriptions ?? 1)
      : ru.pwa.stateGrantedNoSub
    : ru.pwa.stateNotAsked;

  return (
    <div className="bg-gray-50 rounded-2xl p-4" data-testid="push-toggle-row">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <span className="bg-white rounded-full p-2 shrink-0">
            {denied ? <BellOff size={16} /> : <Bell size={16} />}
          </span>
          <div className="min-w-0">
            <p className="font-semibold text-sm">{ru.pwa.pushTitle}</p>
            <p className="text-xs text-gray-500" data-testid="push-state-line">
              {stateLine}
            </p>
          </div>
        </div>
        {!denied && (
          <button
            data-testid="push-toggle-button"
            onClick={toggle}
            disabled={busy}
            aria-pressed={enabled}
            className={`shrink-0 w-12 h-7 rounded-full transition-colors relative ${enabled ? "bg-black" : "bg-gray-300"} disabled:opacity-50`}
          >
            <span
              className={`absolute top-0.5 w-6 h-6 bg-white rounded-full transition-transform ${enabled ? "translate-x-[22px]" : "translate-x-0.5"}`}
            />
          </button>
        )}
      </div>

      {denied && (
        <button onClick={showPushInstructions} className="text-xs font-semibold underline mt-3" data-testid="push-denied-hint">
          {ru.pwa.instructions}
        </button>
      )}
      {server && !server.vapid_configured && (
        <p className="text-xs text-gray-500 mt-3" data-testid="push-server-off">
          {ru.pwa.serverOff}
        </p>
      )}
    </div>
  );
};
